import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useBrands } from '@/lib/BrandsProvider';
import { BlogPostRenderer } from '@/components/content/renderers/BlogPostRenderer';
import { Content } from '@/components/content/useContentData';

// Use the existing Content type from useContentData
type BlogPost = Content;

export default function InternalBlogPost() {
  const { brandId, postId } = useParams<{ brandId: string; postId: string }>();
  const { selectedBrand } = useBrands();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use brandId from URL params or fall back to selectedBrand
  const currentBrandId = brandId || selectedBrand?.id;

  useEffect(() => {
    async function fetchBlogPost() {
      if (!currentBrandId || !postId) {
        setError("Missing brand ID or post ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('content_management_view')
          .select('*')
          .eq('id', postId)
          .eq('brand_id', currentBrandId)
          .eq('content_type', 'blog_post')
          .eq('display_status', 'PUBLISHED')
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          setError("Blog post not found");
          return;
        }

        // Transform the database result to match the Content type
        // The content_management_view provides all necessary fields
        const transformedPost: BlogPost = {
          id: data.id!,
          campaign_id: data.campaign_id!,
          brand_id: data.brand_id!,
          content_type: data.content_type as any,
          current_version: data.current_version!,
          content_json: (data.content_json as any) || {},
          name: data.name!,
          status: data.content_status as any, // Using content_status, not display_status
          created_at: data.created_at!,
          updated_at: data.updated_at!,
          campaigns: null, // This will be null for direct database queries
          convergence: null, // No convergence data for direct database queries
        };
        setPost(transformedPost);
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchBlogPost();
  }, [currentBrandId, postId]);

  // No need for custom rendering - BlogPostRenderer handles everything

  if (!currentBrandId) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">
              Please select a brand to view this blog post.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Back navigation - positioned outside the BlogPostRenderer */}
      <div className="container mx-auto py-4">
        <Link 
          to={`/brands/${currentBrandId}/blog`} 
          className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Blog Posts
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="container mx-auto py-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-red-500 text-center">
                Error loading blog post: {error}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : post ? (
        <BlogPostRenderer content={post} />
      ) : (
        <div className="container mx-auto py-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-center">
                Blog post not found.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}